/**
 *
 * Component: SlideFilter
 * Date: 12/8/2020
 *
 */

import React, {
  useReducer,
  useRef,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';

import { Input, Button, Slider, Text, Tooltip } from 'components/common';
import DateUtils from 'utils/date';
import FilterLayout from 'components/FilterLayout';
import { buildFilterTooltipString, numFormatting } from 'utils/commonFunctions';
import { BUTTON_TOOLTIP } from 'utils/commonConstants';

import styles from './style.css';

function SlideFilter({
  type,
  min,
  max,
  selectedValues,
  filter,
  onFilterApplied,
  moreFilter,
  nameOnTooltip,
}) {
  const slideFilterRef = useRef();

  const isDurationConversionRequired = () =>
    filter.field === 'duration' &&
    localStorage.getItem('tenant') === 'payplanuk';

  /**
   * for "payplanuk" we want to change from seconds to minutes
   */
  const checkIfNeedToConvertToMinutes = valueInSeconds => {
    const convertToMinutes = arg => Math.floor(arg / 60);
    if (isDurationConversionRequired()) {
      return convertToMinutes(valueInSeconds);
    }
    return valueInSeconds;
  };

  const checkIfNeedToRevertToSecondsBeforeAPICall = duration => {
    if (isDurationConversionRequired()) {
      return duration * 60;
    }
    return duration;
  };

  const suffixOnFilter = () => {
    if (filter.field !== 'duration') {
      return '';
    }
    if (isDurationConversionRequired()) {
      return 'm';
    }
    return 's';
  };

  /**
   * Fixes the range overflow and underflow for v.
   * @param {Numer} v: value of the Range to fix, out of Range issues.
   * @returns {Number}: After adjustiong v into the range.
   */
  const getOutsideRangeValue = v => {
    let value = v;
    if (type === 'duration') {
      if (value < minState) {
        value = minState;
      } else if (value > maxState) {
        value = maxState;
      }
    }
    return value;
  };
  /**
   * InitialState Object.
   *  */
  const initialState = {
    minState: checkIfNeedToConvertToMinutes(Math.floor(min)) || 0,
    maxState: checkIfNeedToConvertToMinutes(Math.ceil(max)) ?? 100,
    minValue: checkIfNeedToConvertToMinutes(
      getOutsideRangeValue(
        selectedValues?.length ? selectedValues[0] : Math.floor(min),
      ) || 0,
    ),
    maxValue: checkIfNeedToConvertToMinutes(
      getOutsideRangeValue(
        selectedValues?.length ? selectedValues[1] : Math.ceil(max),
      ) ?? 100,
    ),
    showFilter: false,
    sliderError: false,
    errorMessage: 'Min value cannot be less than 0',
    formatterValue: type === 'amount' ? '$ ' : '',
  };
  if (window.Cypress) {
    window.maxState = maxState;
  }
  /**
   * Checks for Overflow and UnderFlow issues, and represents back the message.
   * @param {String} durationType: Type of duration i.e, duration, etc.
   * @param {*} argMinValue : min value of args
   * @param {*} argMaxValue : max value of args.
   * @returns {Object}
   */
  const checkValueError = (durationType, argMinValue, argMaxValue) => {
    let newSliderError = false;
    let newErrorMessage = '';
    if (type === 'duration') {
      if (argMinValue < 0 || argMaxValue < 0) {
        newSliderError = true;
        newErrorMessage = `${durationType} duration must be greater than 0`;
      } else if (argMaxValue < argMinValue) {
        newSliderError = true;
        newErrorMessage =
          'Maximum duration must be greater than minimum duration';
      }
    }

    return {
      newSliderError,
      newErrorMessage,
    };
  };

  function reducer(state, action) {
    switch (action.type) {
      case 'onMinValueChange': {
        const value = action.payload;
        let newMinValue = value ? parseFloat(value) : 0;
        let newMaxValue = state.maxValue;
        const { newSliderError, newErrorMessage } = checkValueError(
          'Minimum',
          newMinValue,
          newMaxValue,
        );

        if (
          type === 'duration' &&
          (newMinValue < minState || newMinValue > maxState)
        ) {
          newMinValue = getOutsideRangeValue(newMinValue);
          newMaxValue = getOutsideRangeValue(newMaxValue);
        }
        return {
          ...state,
          sliderError: newSliderError,
          errorMessage: newErrorMessage,
          minValue: newMinValue,
          maxValue: newMaxValue,
        };
      }
      case 'onMaxValueChange': {
        const value = action.payload;
        let newMaxValue = value ? parseFloat(value) : 0;
        let newMinValue = state.minValue;
        const { newSliderError, newErrorMessage } = checkValueError(
          'Maximum',
          newMinValue,
          newMaxValue,
        );
        if (newSliderError) {
          return { ...state, maxValue: newMaxValue };
        }
        if (
          type === 'duration' &&
          (newMaxValue < minState || newMaxValue > maxState)
        ) {
          newMinValue = getOutsideRangeValue(newMinValue);
          newMaxValue = getOutsideRangeValue(newMaxValue);
        }
        return {
          ...state,
          sliderError: newSliderError,
          errorMessage: newErrorMessage,
          minValue: newMinValue,
          maxValue: newMaxValue,
        };
      }
      case 'onSliderChange': {
        const values = action.payload;
        return {
          ...state,
          minValue: values[0],
          maxValue: values[1],
        };
      }
      case 'clear': {
        return {
          ...state,
          ...initialState,
        };
      }
      case 'showFilter':
        return {
          ...state,
          showFilter: action.payload,
        };
      default:
        throw new Error();
    }
  }

  const [state, dispatch] = useReducer(reducer, initialState);

  const {
    minState,
    maxState,
    minValue,
    maxValue,
    showFilter,
    sliderError,
    errorMessage,
    formatterValue,
  } = state;

  /**
   * Debouncing in FilterApplied.
   */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const delayedQuery = useCallback(
    // This is used to prevent the performance hit of slider filter
    debounce((field, e) => onFilterApplied(field, e), 100),
    [],
  );

  useEffect(() => {
    if (moreFilter) {
      delayedQuery(filter.field, [
        checkIfNeedToRevertToSecondsBeforeAPICall(minValue),
        checkIfNeedToRevertToSecondsBeforeAPICall(maxValue),
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minValue, maxValue, moreFilter]);

  const processSelectedValueByType = valueArg => {
    let value;
    if (filter.subtype === 'duration') {
      value = DateUtils.formatDuration(valueArg);
    } else if (filter.subtype === 'amount') {
      value = `$${numFormatting(valueArg)}`;
    } else if (filter.field === 'duration') {
      value = numFormatting(checkIfNeedToConvertToMinutes(valueArg));
    } else {
      value = numFormatting(valueArg);
    }
    return value;
  };

  /**
   * Function to get Button name as it is variable depending on selected values
   *
   * @return -> button name
   *
   */
  const getButtonName = () => {
    let filterName = filter.name;
    const selectedValue = selectedValues;
    if (selectedValue?.length) {
      if (min === selectedValue[0] && max === selectedValue[1]) {
        filterName = filter.name;
      } else if (min !== selectedValue[0] && max !== selectedValue[1]) {
        filterName = `${processSelectedValueByType(
          selectedValue[0],
        )} - ${processSelectedValueByType(selectedValue[1])}`;
      } else if (min === selectedValue[0]) {
        filterName = `Less than ${processSelectedValueByType(
          selectedValue[1],
        )}`;
      } else {
        filterName = `More than ${processSelectedValueByType(
          selectedValue[0],
        )}`;
      }
    } else {
      filterName = filter.name;
    }

    return filterName;
  };

  const getFormatter = value => {
    if (filter.field === 'duration') {
      return `${value} ${suffixOnFilter()}`;
    }
    return `${formatterValue}${value}`;
  };

  const getParser = value => {
    if (filter.field === 'duration') {
      return value.replaceAll(/\s?[sm]+/g, '');
    }
    return value.replace(/\$|:|,/gi, '');
  };

  /**
   * returns JSX.
   * */
  const getFilter = () => (
    <div className={styles.sliderFilterContent}>
      <div className={styles.sliderContainer} data-testid="slider-cy">
        <Slider
          range
          min={checkIfNeedToConvertToMinutes(Math.floor(min))}
          max={checkIfNeedToConvertToMinutes(Math.ceil(max))}
          tooltipVisible={false}
          defaultValue={[minValue, maxValue]}
          value={[minValue, maxValue]}
          onChange={e => {
            dispatch({ type: 'onSliderChange', payload: e });
          }}
        />
      </div>
      <div className={styles.sliderInputContainer}>
        <Input
          data-testid="min-input-cy"
          type="number"
          min={Math.floor(min)}
          formatter={getFormatter}
          parser={getParser}
          className="more-slider-input-box"
          value={minValue}
          onChange={e => {
            dispatch({ type: 'onMinValueChange', payload: e });
          }}
        />
        <Input
          data-testid="max-input-cy"
          type="number"
          max={Math.floor(max)}
          formatter={getFormatter}
          parser={getParser}
          className="more-slider-input-box"
          value={maxValue}
          onChange={e => {
            dispatch({ type: 'onMaxValueChange', payload: e });
          }}
        />
      </div>
      {sliderError && (
        <p data-testid="error-cy" className="error-message-span">
          {errorMessage}
        </p>
      )}
    </div>
  );

  /**
   * @returns {JSX} based showFilter or moreFilter.
   */
  const getFilterComponent = () => {
    let component;
    if (showFilter || moreFilter) {
      if (moreFilter) {
        /**
         *as slide filter don't need layout buttons in morefilters
         */
        component = getFilter();
      } else {
        component = (
          <FilterLayout
            data-testid="sliderfilter-cy"
            onCancel={() => handleClickOutside()}
            disabled={sliderError}
            onApply={e => {
              e.stopPropagation();
              onFilterApplied(filter.field, [
                checkIfNeedToRevertToSecondsBeforeAPICall(minValue),
                checkIfNeedToRevertToSecondsBeforeAPICall(maxValue),
              ]);
              handleClickOutside();
            }}
            onClear={e => {
              e.stopPropagation();
              onFilterApplied(filter.field, [Math.floor(min), Math.ceil(max)]);
              dispatch({ type: 'clear' });
              handleClickOutside();
            }}
          >
            {getFilter()}
          </FilterLayout>
        );
      }
    }
    return component;
  };

  /**
   * dispatches into reducer and sets up the listener.
   */
  const handleClickOutside = () => {
    dispatch({ payload: false, type: 'showFilter' });
    handleListener(false);
  };

  /**
   * starts or removes the event listener for mouse clicks.
   * @param {Boolean} bool : Start or remove the listener.
   */
  const handleListener = bool => {
    if (bool) {
      document.addEventListener('mousedown', handleClickEvent);
    } else {
      document.removeEventListener('mousedown', handleClickEvent);
    }
  };

  /**
   * Detects outside click than component.
   * @param {HTMLElement} e
   */
  const handleClickEvent = e => {
    const path = e.path || (e.composedPath && e.composedPath());
    if (!path.includes(slideFilterRef.current)) {
      handleClickOutside();
    }
  };

  const memoizedToolTipName = useMemo(
    () => buildFilterTooltipString(filter?.name),
    [filter],
  );

  return (
    <div
      data-testid={`slider-filter-cy-${filter.field}`}
      ref={slideFilterRef}
      className={styles.container}
    >
      {moreFilter && <Text type="subtitle" text={filter?.name} />}
      {!moreFilter && (
        <Tooltip
          type={BUTTON_TOOLTIP}
          title={`Search ${nameOnTooltip} by ${memoizedToolTipName}`}
          disabled={!nameOnTooltip}
        >
          <Button
            text={getButtonName()}
            height="25px"
            type={`chips${selectedValues?.length ? '' : '-secondary'}`}
            onClick={() => {
              dispatch({ payload: !showFilter, type: 'showFilter' });
              handleListener(!showFilter);
            }}
          />
        </Tooltip>
      )}
      {getFilterComponent()}
    </div>
  );
}

SlideFilter.propTypes = {
  type: PropTypes.string,
  min: PropTypes.number,
  max: PropTypes.number,
  selectedValues: PropTypes.array,
  filter: PropTypes.object,
  onFilterApplied: PropTypes.func,
  moreFilter: PropTypes.bool,
  nameOnTooltip: PropTypes.string,
};

export default SlideFilter;
